/**
 * CMS Content Block (Content Card) Queries
 *
 * Server-side query for fetching a single reusable content card by id.
 * Used by the /preview/content-card/[id] route for CMS live preview (ADE-908).
 */

import { query } from "@solidjs/router";
import type { CMSTextAndMediaCTABlock } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS } from "../queryUtils";

/** Content card doc as returned by the collection API (component-compatible fields + tenant) */
type CMSContentCardDoc = CMSTextAndMediaCTABlock & {
  id: number;
  name?: string;
  tenant?: { slug?: string } | number | null;
};

/**
 * Server-side query for fetching a content card by id
 *
 * @param id - The content-blocks document id
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Content card data or null if not found / wrong tenant
 */
export const getContentBlockByIdQuery = query(async (id: string, previewToken?: string) => {
  "use server";
  const draft = isPreviewTokenValid(previewToken, env.tenantSlug);

  const LOG_PREFIX = "[contentBlockById]";

  const url = `${env.cmsApiUrl}/api/content-blocks/${encodeURIComponent(id)}?depth=2${
    draft ? "&draft=true" : ""
  }`;

  const doc = await fetchFromCMS<CMSContentCardDoc>(url, {
    logPrefix: LOG_PREFIX,
    previewToken: draft ? previewToken : undefined,
  });

  if (!doc?.id) {
    logger.warn(`${LOG_PREFIX} No content card found with id: ${id}`);
    return null;
  }

  // Only render cards belonging to this site's tenant
  const tenantSlug = typeof doc.tenant === "object" ? doc.tenant?.slug : undefined;
  if (tenantSlug && tenantSlug !== env.tenantSlug) {
    logger.warn(`${LOG_PREFIX} Content card ${id} belongs to tenant ${tenantSlug}, skipping`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Found content card: ${doc.name || doc.headline || id}`);
  return doc;
}, "contentBlockById");
